import { useEffect, useMemo, useState } from 'react';
import { analyzeBazi, calculateBaziAsync } from '../utils/bazi';
import { parseLocalDate } from '../utils/dateUtils';
import { buildDetailedReading } from '../view-models/baziReadingModel';

export default function useBaziAnalysis({
  birthDate,
  birthHour,
  timezoneOffsetMinutes,
  gender,
  language
}) {
  const [bazi, setBazi] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const parsedDate = useMemo(() => parseLocalDate(birthDate), [birthDate]);
  const analysis = useMemo(() => (bazi ? analyzeBazi(bazi) : null), [bazi]);
  const detailedReading = useMemo(
    () => (bazi && analysis
      ? buildDetailedReading({
        bazi,
        analysis,
        birthDate: parsedDate,
        birthHour,
        language,
        timezoneOffsetMinutes,
        gender
      })
      : null),
    [analysis, bazi, birthHour, gender, language, parsedDate, timezoneOffsetMinutes]
  );

  useEffect(() => {
    let isActive = true;

    if (!parsedDate) {
      setBazi(null);
      setIsLoading(false);
      return () => {
        isActive = false;
      };
    }

    setIsLoading(true);
    calculateBaziAsync(parsedDate, birthHour, { timezoneOffsetMinutes, gender })
      .then((result) => {
        if (isActive) setBazi(result);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('[Bazi] Failed to calculate report', error);
        if (isActive) setBazi(null);
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [birthHour, gender, parsedDate, timezoneOffsetMinutes]);

  return {
    bazi,
    isLoading,
    parsedDate,
    analysis,
    detailedReading
  };
}
